import { Box } from '@dealstack/frontend/shared/components/Box/Box';
import { CssValue } from '@dealstack/shared/lib/frontend/common-ui/types';
import React, { BaseSyntheticEvent, DOMAttributes, memo, useCallback } from 'react';
import ReactModal from 'react-modal';
import styled, { css } from 'styled-components';

interface Props {
	size?: 'sm' | 'md' | 'lg';
	children: React.ReactNode;
	className?: string;
	onClose: (event: BaseSyntheticEvent) => unknown;
	onClick?: DOMAttributes<HTMLElement>['onClick'];
	reactModalProps?: Partial<ReactModal.Props>;
	customWidth?: CssValue;
	disableScroll?: boolean;
}

const SCROLLBAR_VISIBLE_CLASS = 'scrollbarVisible';

const ModalV2 = ({
	className,
	onClick,
	onClose,
	children,
	reactModalProps,
	disableScroll,
}: Props) => {
	/**
	 * We check if scrollbar is visible, then we change
	 * it's sizing
	 */
	const modalRef = useCallback(
		(node: HTMLDivElement) => {
			if (!node) {
				return;
			}
			const isScrollbarVisible = node.scrollHeight > node.clientHeight;
			if (isScrollbarVisible) {
				node.classList.add(SCROLLBAR_VISIBLE_CLASS);
			} else {
				node.classList.remove(SCROLLBAR_VISIBLE_CLASS);
			}
		},
		[children]
	);

	return (
		<ReactModal
			contentLabel="modal"
			isOpen
			onRequestClose={onClose}
			className={className}
			{...reactModalProps}
		>
			<ModalBox disableScroll={disableScroll} ref={modalRef} onClick={onClick}>
				<ModalContentV2>{children}</ModalContentV2>
			</ModalBox>
		</ReactModal>
	);
};

export const ModalContentV2 = styled.div``;
export const ModalHeader = styled.div``;
const ModalBox = styled(Box)<{ disableScroll }>`
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	${({ theme }) => theme.shadow.z1}
	padding: 1rem;
	&.${SCROLLBAR_VISIBLE_CLASS} {
		padding-right: 0.5rem;
	}
	border-radius: 0.5rem;
`;

const Memoized = memo(ModalV2);
const Styled = styled(Memoized)`
	${ModalBox} {
		overflow: ${({ disableScroll }) => (disableScroll ? 'hidden' : 'auto')};
		max-height: 80vh;

		${({ customWidth, size }) => {
			if (customWidth) {
				return css`
					width: ${customWidth};
				`;
			}
			switch (size) {
				case 'sm':
					return `
                    width: clamp(12rem, 30vw, 30rem);
                `;
				case 'md':
					return `
                    width: clamp(12rem, 50vw, 60rem);
                `;
				case 'lg':
					return `
                    width: clamp(12rem, 80vw, 100rem);
                `;

				default:
					return `width: 22.5rem`;
			}
		}}
	}
`;

export { Styled as ModalV2 };
